/**
 * Transcript timestamp helpers.
 *
 * The knowledge base labels every line "MM:SS", and keeps counting in minutes
 * past the hour for long discourses (e.g. "155:32" for a 2h35m video), so a
 * two-part label whose minutes exceed 59 is valid and must be preserved.
 */

/** Parse an "MM:SS" (or "HH:MM:SS") label to whole seconds; 0 when malformed. */
export function parseTimestamp(label: string): number {
  const parts = label.trim().split(":");
  const numbers = parts.map((part) => Number.parseInt(part, 10));
  if (numbers.length === 0 || numbers.some((n) => Number.isNaN(n))) {
    return 0;
  }
  if (numbers.length === 1) {
    return numbers[0];
  }
  if (numbers.length === 2) {
    return numbers[0] * 60 + numbers[1];
  }
  return numbers[0] * 3600 + numbers[1] * 60 + numbers[2];
}

/** Format whole seconds for display: "M:SS", or "H:MM:SS" once past an hour. */
export function formatTimestamp(totalSeconds: number): string {
  const whole = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const seconds = whole % 60;
  const ss = seconds.toString().padStart(2, "0");
  if (hours > 0) {
    const mm = minutes.toString().padStart(2, "0");
    return `${hours}:${mm}:${ss}`;
  }
  return `${minutes}:${ss}`;
}

/** Human duration for a card: "3 min", "1 hr 12 min", "48 sec". */
export function formatDuration(totalSeconds: number): string {
  const whole = Math.max(0, Math.round(totalSeconds));
  if (whole < 60) {
    return `${whole} sec`;
  }
  const hours = Math.floor(whole / 3600);
  const minutes = Math.round((whole % 3600) / 60);
  if (hours > 0) {
    return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
  }
  return `${minutes} min`;
}
